import React, { useState, useEffect } from 'react';
import Icon from '../components/Icon';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import integrationService from '../services/integrationService';
import Notification from '../components/Notification';
import useNotification from '../hooks/useNotification';

const IntegrationsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.1rem;
  margin: 1rem 0 2rem 0;
  line-height: 1.6;
`;



const IntegrationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const IntegrationCard = styled(motion.div)`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {    box-shadow: var(--shadow-xl);
    border-color: var(--accent-cyan);
  }


`;

const IntegrationIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: var(--radius-lg);
  background: ${props => props.$bgColor || 'var(--gradient-primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-bottom: 1rem;
  box-shadow: var(--shadow-md);
`;

const IntegrationName = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
`;

const IntegrationDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const IntegrationStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  
  &.available {
    color: var(--accent-cyan);
  }
  
  &.coming-soon {
    color: var(--text-muted);
  }
  
  &.connected {
    color: var(--success-color);
  }
  
  &.disconnected {
    color: var(--text-muted);
  }
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 50px;
  height: 26px;
  background: ${props => props.$isOn ? 'var(--success-color)' : 'var(--border-strong)'};
  border-radius: 26px;
  cursor: pointer;
  transition: background 0.3s ease;
  
  &:before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isOn ? '26px' : '2px'};
    width: 22px;
    height: 22px;
    background: var(--text-primary);
    border-radius: 50%;
    transition: left 0.3s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
  
  &:hover {
    opacity: 0.8;
  }
`;



// Modal Styles
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const ModalContainer = styled(motion.div)`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  backdrop-filter: blur(20px);
  box-shadow: var(--shadow-xl);
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0.5rem;
  border-radius: var(--radius-md);
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  background: rgba(22, 26, 43, 0.8);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.75rem;
  color: var(--text-primary);
  font-size: 1rem;
  transition: var(--transition-normal);
  font-family: var(--font-primary);

  &:focus {
    outline: none;
    border-color: var(--accent-cyan);
    box-shadow: var(--shadow-glow);
    background: rgba(22, 26, 43, 0.9);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const Select = styled.select`
  background: rgba(22, 26, 43, 0.8);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.75rem;
  color: var(--text-primary);
  font-size: 1rem;
  transition: var(--transition-normal);
  font-family: var(--font-primary);

  &:focus {
    outline: none;
    border-color: var(--accent-cyan);
    box-shadow: var(--shadow-glow);
    background: rgba(22, 26, 43, 0.9);
  }

  option {
    background: var(--primary-bg);
    color: var(--text-primary);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
`;

const Button = styled(motion.button)`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  &.primary {
    background: var(--accent-cyan);
    color: var(--primary-bg);
    
    &:hover {      box-shadow: var(--shadow-md);
    }
  }
  
  &.secondary {
    background: transparent;
    color: var(--text-secondary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background: rgba(255, 255, 255, 0.05);
      color: var(--text-primary);
    }
  }
`;

/**
 * Email finders the user can bring their own key for.
 *
 * Orion tries them in order after any address you already supplied, and stops
 * at the first verified hit, so connecting more than one only improves
 * coverage. Adding a provider is an entry here plus a small file on the server;
 * nothing else in the app names a vendor.
 */
const EMAIL_FINDERS = [
  {
    id: 'hunter',
    name: 'Hunter.io',
    icon: 'search',
    bgColor: 'linear-gradient(135deg, #FF8C42 0%, #FFB88C 100%)',
    description: 'Find and verify work email addresses for your leads',
    helpUrl: 'https://hunter.io/api-keys',
    note: 'Hunter charges nothing for a lookup that finds nothing. Orion also runs '
      + 'every address through Hunter\'s verifier and discards anything it cannot '
      + 'confirm, including catch-all domains.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', required: true, placeholder: 'Your Hunter API key' }
    ]
  },
  {
    id: 'clay',
    name: 'Clay',
    icon: 'layers',
    bgColor: 'linear-gradient(135deg, #6EE7B7 0%, #60A5FA 100%)',
    description: 'Find leads in Clay’s GTM database, with company domains',
    helpUrl: 'https://app.clay.com/settings/account',
    note: 'Create the key in Clay under Settings → Account → API keys. It starts '
      + 'with "clay_scoped_". A token from the Clay CLI starts with "clay_user_" '
      + 'and will not work here - it is a session, not an API key. Search works '
      + 'on the free plan with no routine at all.',
    steps: [
      'In Clay, open Functions and press New → Browse Clay managed functions.',
      'Pick the field you want. "Work Email" costs about 1.1 credits a row; the combined contact-details one costs about 12.8, because the mobile number is most of it.',
      'Press "Setup as managed", tick "API & CLI", then Continue.',
      'Open the function and copy the Routine ID under Integrations - it looks like "function:t_...".',
      'Paste it into the matching field below. Each field is a separate routine, so you only pay for the ones you add.'
    ],
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', required: true, placeholder: 'clay_scoped_...' },
      { key: 'routineId', label: 'Work email routine (optional)', type: 'text', required: false, placeholder: 'enrichment:... or function:t_...' },
      { key: 'phoneRoutineId', label: 'Mobile phone routine (optional)', type: 'text', required: false, placeholder: 'enrichment:... or function:t_...' }
    ]
  }
];

const Integrations = () => {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isApolloModalOpen, setIsApolloModalOpen] = useState(false);
  const [activeFinder, setActiveFinder] = useState(null);
  const [finderForm, setFinderForm] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [existingIntegration, setExistingIntegration] = useState(null);
  const [integrationStatuses, setIntegrationStatuses] = useState({});
  const [isToggling, setIsToggling] = useState(false);
  const { notifications, removeNotification, showSuccess, showError, showInfo } = useNotification();

  const [formData, setFormData] = useState({
    smtpHost: '',
    smtpPort: '587',
    smtpSecurity: 'STARTTLS',
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    apolloApiKey: '' // Added for Apollo
  });

  // Load integration statuses on component mount
  useEffect(() => {
    loadIntegrationStatuses();
  }, []);

  const loadIntegrationStatuses = async () => {
    try {
      const response = await integrationService.getIntegrations();
      if (response.success) {
        const statusMap = {};
        response.data.integrations.forEach(integration => {
          statusMap[integration.type] = {
            isConnected: integration.status === 'active',
            lastTest: integration.lastTest,
            stats: integration.stats
          };
        });
        setIntegrationStatuses(statusMap);
      }
    } catch (error) {
      console.error('Error loading integration statuses:', error);
    }
  };

  const handleToggleIntegration = async (integrationType, currentStatus) => {
    if (isToggling) return;

    try {
      setIsToggling(true);

      if (currentStatus) {
        // Deactivate integration
        await integrationService.deactivateIntegration(integrationType);
        showInfo(`${integrationType} integration deactivated`);
      } else {
        // Check if integration exists before activating
        const integrationResponse = await integrationService.getIntegrationByType(integrationType);
        if (!integrationResponse.success || !integrationResponse.data) {
          showError(`Please configure ${integrationType} integration first`);
          if (integrationType === 'email') {
            setIsEmailModalOpen(true);
          }
          return;
        }

        // Activate integration
        await integrationService.activateIntegration(integrationType);
        showSuccess(`${integrationType} integration activated`);
      }

      // Reload statuses
      await loadIntegrationStatuses();
    } catch (error) {
      showError(error.message || `Failed to toggle ${integrationType} integration`);
    } finally {
      setIsToggling(false);
    }
  };

  const integrations = [
    {
      id: 'email',
      name: 'Email (SMTP)',
      description: 'Connect your email server to send automated emails and notifications',
      icon: 'mail',
      bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      status: 'available',
      onClick: () => setIsEmailModalOpen(true)
    },
    {
      id: 'apollo',
      name: 'Apollo',
      description: 'Connect your Apollo account to find and enrich leads',
      icon: 'spark',
      bgColor: 'linear-gradient(135deg, #FF9A9E 0%, #FECFEF 99%, #FECFEF 100%)',
      status: 'available',
      onClick: () => setIsApolloModalOpen(true)
    },
    ...EMAIL_FINDERS.map((finder) => ({
      id: finder.id,
      name: finder.name,
      description: finder.description,
      icon: finder.icon,
      bgColor: finder.bgColor,
      status: 'available',
      onClick: () => openFinderModal(finder)
    }))
  ];

  // Load existing email integration when modal opens
  useEffect(() => {
    if (isEmailModalOpen) {
      loadExistingIntegration();
    }
  }, [isEmailModalOpen]);

  const loadExistingIntegration = async () => {
    try {
      setIsLoading(true);
      const response = await integrationService.getIntegrationByType('email');

      if (response.success && response.data.integration) {
        const integration = response.data.integration;
        setExistingIntegration(integration);

        // Populate form with existing data
        setFormData({
          smtpHost: integration.config.smtpHost || '',
          smtpPort: integration.config.smtpPort || '587',
          smtpSecurity: integration.config.smtpSecurity || 'STARTTLS',
          username: integration.config.username || '',
          password: '', // Don't populate password for security
          fromEmail: integration.config.fromEmail || '',
          fromName: integration.config.fromName || ''
        });
      }
    } catch (error) {
      console.error('Error loading integration:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTestConnection = async () => {
    try {
      setIsTesting(true);

      // Validate required fields
      const requiredFields = ['smtpHost', 'smtpPort', 'username', 'password', 'fromEmail'];
      const missingFields = requiredFields.filter(field => !formData[field]);

      if (missingFields.length > 0) {
        showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      const response = await integrationService.testIntegration('email', formData);

      if (response.success && response.data.testResult.success) {
        showSuccess('SMTP connection test successful! Configuration is working correctly.');
      } else {
        showError(response.data.testResult.message || 'Connection test failed');
      }
    } catch (error) {
      showError(error.message || 'Failed to test connection');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setIsTesting(true);

      // Validate required fields
      const requiredFields = ['smtpHost', 'smtpPort', 'username', 'password', 'fromEmail'];
      const missingFields = requiredFields.filter(field => !formData[field]);

      if (missingFields.length > 0) {
        showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      const response = await integrationService.sendTestEmail(formData);

      if (response.success) {
        showSuccess(`Test email sent successfully to ${formData.fromEmail}! Check your inbox.`);
      } else {
        showError(response.message || 'Failed to send test email');
      }
    } catch (error) {
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      const integrationData = {
        type: 'email',
        name: 'Email SMTP Integration',
        config: formData,
        metadata: {
          description: 'SMTP email integration for sending automated emails',
          version: '1.0.0'
        }
      };

      const response = await integrationService.createOrUpdateIntegration(integrationData);

      if (response.success) {
        showSuccess(existingIntegration ? 'Email integration updated successfully!' : 'Email integration created successfully!');
        // Activate the integration
        await integrationService.activateIntegration('email');
        showInfo('Email integration activated and ready to use.');
        // Reload integration statuses
        await loadIntegrationStatuses();
        setIsEmailModalOpen(false);
      } else {
        showError(response.message || 'Failed to save integration');
      }
    } catch (error) {
      showError(error.message || 'Failed to save integration');
    } finally {
      setIsSaving(false);
    }
  };



  const openFinderModal = async (finder) => {
    setFinderForm({});
    setActiveFinder(finder);

    // Load what is already saved, so adding one field does not mean retyping
    // the others. Secrets are never returned in the clear, so a saved key is
    // shown as saved rather than refilled - submitting the field empty keeps
    // the stored value, which the server already handles.
    try {
      const existing = await integrationService.getIntegrationByType(finder.id);
      const config = (existing && existing.data && existing.data.integration
        && existing.data.integration.config) || {};

      const prefill = {};
      finder.fields.forEach((field) => {
        const isSecret = field.type === 'password';
        if (!isSecret && config[field.key]) prefill[field.key] = config[field.key];
        if (isSecret && config[field.key]) prefill[`__saved_${field.key}`] = true;
      });
      setFinderForm(prefill);
    } catch (error) {
      // A first-time connection has nothing to load; the blank form is correct.
    }
  };

  const closeFinderModal = () => {
    setActiveFinder(null);
    setFinderForm({});
  };

  /**
   * One handler for every email finder. Providers differ only by the fields
   * they declare in EMAIL_FINDERS, so adding another is an array entry rather
   * than another modal.
   */
  const handleFinderSubmit = async (e) => {
    e.preventDefault();
    if (!activeFinder) return;

    try {
      setIsSaving(true);
      const config = {};
      activeFinder.fields.forEach(({ key }) => {
        const value = (finderForm[key] || '').trim();
        // Omitted when blank: the server keeps the stored secret rather than
        // overwriting it with nothing. __saved_ markers are UI state, not config.
        if (value) config[key] = value;
      });

      const response = await integrationService.createOrUpdateIntegration({
        type: activeFinder.id,
        name: `${activeFinder.name} Integration`,
        config,
        metadata: { description: activeFinder.description, version: '1.0.0' }
      });

      if (response.success) {
        showSuccess(`${activeFinder.name} connected. It will be used to find emails your other sources miss.`);
        await integrationService.activateIntegration(activeFinder.id);
        await loadIntegrationStatuses();
        closeFinderModal();
      }
    } catch (error) {
      showError(error.message || `Failed to save the ${activeFinder.name} integration`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApolloSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      const integrationData = {
        type: 'apollo',
        name: 'Apollo Integration',
        config: { apiKey: formData.apolloApiKey },
        metadata: {
          description: 'Access 250M+ contacts for lead generation',
          version: '1.0.0'
        }
      };

      const response = await integrationService.createOrUpdateIntegration(integrationData);

      if (response.success) {
        showSuccess('Apollo integration saved successfully!');
        await integrationService.activateIntegration('apollo');
        await loadIntegrationStatuses();
        setIsApolloModalOpen(false);
      }
    } catch (error) {
      showError(error.message || 'Failed to save Apollo integration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApolloClose = () => {
    setIsApolloModalOpen(false);
    setFormData(prev => ({ ...prev, apolloApiKey: '' }));
  };

  // ... (rest of render)


  const handleCloseModal = () => {
    setIsEmailModalOpen(false);
    // Reset form data
    setFormData({
      smtpHost: '',
      smtpPort: '587',
      smtpSecurity: 'STARTTLS',
      username: '',
      password: '',
      fromEmail: '',
      fromName: ''
    });
    setExistingIntegration(null);
  };

  return (
    <IntegrationsContainer>
      <Header>
        <div>
          <Title>Integrations</Title>
          <Subtitle>
            Connect your favorite tools and platforms to streamline your sales workflow
          </Subtitle>
        </div>
      </Header>

      <IntegrationsGrid>
        {integrations.map((integration) => {
          const integrationStatus = integrationStatuses[integration.id];
          const isConnected = integrationStatus?.isConnected || false;

          return (
            <IntegrationCard
              key={integration.id}
              onClick={integration.status === 'available' ? integration.onClick : undefined}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ scale: integration.status === 'available' ? 1.02 : 1 }}
              whileTap={{ scale: integration.status === 'available' ? 0.98 : 1 }}
              style={{ cursor: integration.status === 'available' ? 'pointer' : 'default' }}
            >
              <IntegrationIcon $bgColor={integration.bgColor}>
                <Icon name={integration.icon} size={22} />
              </IntegrationIcon>

              <IntegrationName>{integration.name}</IntegrationName>
              <IntegrationDescription>{integration.description}</IntegrationDescription>

              <IntegrationStatus>
                <StatusInfo className={
                  integration.status === 'available'
                    ? (isConnected ? 'connected' : 'available')
                    : 'coming-soon'
                }>
                  {integration.status === 'available' ? (
                    isConnected ? (
                      <>
                        <Icon name="check" size={14} />
                        Connected
                      </>
                    ) : (
                      <>
                        Available
                      </>
                    )
                  ) : (
                    <>
                      <Icon name="clock" size={14} />
                      Coming Soon
                    </>
                  )}
                </StatusInfo>

                {integration.status === 'available' && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ToggleSwitch
                      $isOn={isConnected}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleIntegration(integration.id, isConnected);
                      }}
                    />
                  </div>
                )}
              </IntegrationStatus>

              {integrationStatus?.stats && isConnected && (
                <div style={{
                  marginTop: '0.75rem',
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                  borderTop: '1px solid var(--border-color)',
                  paddingTop: '0.75rem'
                }}>
                  <div>Total sent: {integrationStatus.stats.totalUsage || 0}</div>
                  <div>Success rate: {
                    integrationStatus.stats.totalUsage > 0
                      ? Math.round((integrationStatus.stats.successCount / integrationStatus.stats.totalUsage) * 100)
                      : 0
                  }%</div>
                </div>
              )}
            </IntegrationCard>
          );
        })}
      </IntegrationsGrid>

      {/* Email SMTP Configuration Modal */}
      <AnimatePresence>
        {isEmailModalOpen && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <ModalContainer
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  <Icon name="mail" size={18} /> Email SMTP Configuration
                </ModalTitle>
                <CloseButton onClick={handleCloseModal}>
                  ×
                </CloseButton>
              </ModalHeader>

              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid var(--border-color)',
                    borderTop: '3px solid var(--accent-cyan)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 1rem'
                  }}></div>
                  <p style={{ color: 'var(--text-secondary)' }}>Loading integration settings...</p>
                </div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label htmlFor="smtpHost">SMTP Host *</Label>
                    <Input
                      id="smtpHost"
                      name="smtpHost"
                      type="text"
                      placeholder="smtp.gmail.com"
                      value={formData.smtpHost}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="smtpPort">SMTP Port *</Label>
                    <Input
                      id="smtpPort"
                      name="smtpPort"
                      type="number"
                      placeholder="587"
                      value={formData.smtpPort}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="smtpSecurity">Security *</Label>
                    <Select
                      id="smtpSecurity"
                      name="smtpSecurity"
                      value={formData.smtpSecurity}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="STARTTLS">STARTTLS</option>
                      <option value="SSL/TLS">SSL/TLS</option>
                      <option value="None">None</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="username">Username *</Label>
                    <Input
                      id="username"
                      name="username"
                      type="text"
                      placeholder="your-email@gmail.com"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Your email password or app password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="fromEmail">From Email *</Label>
                    <Input
                      id="fromEmail"
                      name="fromEmail"
                      type="email"
                      placeholder="noreply@yourcompany.com"
                      value={formData.fromEmail}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="fromName">From Name</Label>
                    <Input
                      id="fromName"
                      name="fromName"
                      type="text"
                      placeholder="Your Company Name"
                      value={formData.fromName}
                      onChange={handleInputChange}
                    />
                  </FormGroup>

                  <ModalActions>
                    <Button
                      type="button"
                      className="secondary"
                      onClick={handleTestConnection}
                      disabled={isTesting || isSaving}
                    >
                      {isTesting ? 'Testing...' : 'Test Connection'}
                    </Button>

                    <Button
                      type="button"
                      className="secondary"
                      onClick={handleSendTestEmail}
                      disabled={isTesting || isSaving}
                    >
                      {isTesting ? 'Sending...' : 'Send Test Email'}
                    </Button>
                  </ModalActions>

                  <ModalActions style={{ marginTop: '1rem' }}>
                    <Button
                      type="button"
                      className="secondary"
                      onClick={handleCloseModal}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="primary"
                      disabled={isSaving || isTesting}
                    >
                      {isSaving ? 'Saving...' : existingIntegration ? 'Update Configuration' : 'Save Configuration'}
                    </Button>
                  </ModalActions>
                </Form>
              )}
            </ModalContainer>
          </ModalOverlay>
        )}
      </AnimatePresence>
      {/* Email finder modal, shared by every BYOK provider */}
      <AnimatePresence>
        {activeFinder && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeFinderModal}
          >
            <ModalContainer
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  <Icon name={activeFinder.icon} size={18} /> {activeFinder.name}
                </ModalTitle>
                <CloseButton onClick={closeFinderModal}>×</CloseButton>
              </ModalHeader>

              <Form onSubmit={handleFinderSubmit}>
                {activeFinder.fields.map((field) => (
                  <FormGroup key={field.key}>
                    <Label htmlFor={field.key}>
                      {field.label}
                      {field.required && !finderForm[`__saved_${field.key}`] ? ' *' : ''}
                      {finderForm[`__saved_${field.key}`] && (
                        <span style={{ color: 'var(--success-color, #34d399)', fontWeight: 400, marginLeft: '0.4rem' }}>
                          saved
                        </span>
                      )}
                    </Label>
                    <Input
                      id={field.key}
                      name={field.key}
                      type={field.type}
                      placeholder={finderForm[`__saved_${field.key}`]
                        ? 'Leave blank to keep the saved key'
                        : field.placeholder}
                      value={finderForm[field.key] || ''}
                      onChange={(e) => setFinderForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      required={field.required && !finderForm[`__saved_${field.key}`]}
                    />
                  </FormGroup>
                ))}

                {activeFinder.steps && (
                  <ol style={{
                    fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.65,
                    margin: '-0.5rem 0 0', paddingLeft: '1.1rem'
                  }}>
                    {activeFinder.steps.map((step, i) => (
                      <li key={i} style={{ marginBottom: '0.35rem' }}>{step}</li>
                    ))}
                  </ol>
                )}

                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.6, marginTop: '-0.5rem' }}>
                  {activeFinder.note}
                  {' '}Credentials are encrypted before they are stored.
                  {activeFinder.helpUrl && (
                    <>
                      {' '}
                      <a
                        href={activeFinder.helpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--accent-cyan)' }}
                      >
                        Where do I find this?
                      </a>
                    </>
                  )}
                </p>

                <ModalActions>
                  <Button type="button" className="secondary" onClick={closeFinderModal} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button type="submit" className="primary" disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Connect'}
                  </Button>
                </ModalActions>
              </Form>
            </ModalContainer>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Apollo Configuration Modal */}
      <AnimatePresence>
        {isApolloModalOpen && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleApolloClose}
          >
            <ModalContainer
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  <Icon name="spark" size={18} /> Apollo Configuration
                </ModalTitle>
                <CloseButton onClick={handleApolloClose}>
                  ×
                </CloseButton>
              </ModalHeader>

              <Form onSubmit={handleApolloSubmit}>
                <FormGroup>
                  <Label htmlFor="apolloApiKey">Apollo API Key *</Label>
                  <Input
                    id="apolloApiKey"
                    name="apolloApiKey"
                    type="password"
                    placeholder="Enter your Apollo API Key"
                    value={formData.apolloApiKey}
                    onChange={handleInputChange}
                    required
                  />
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                    Your key will be encrypted and stored securely.
                  </p>
                </FormGroup>

                <ModalActions>
                  <Button
                    type="button"
                    className="secondary"
                    onClick={handleApolloClose}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="primary"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save & Connect'}
                  </Button>
                </ModalActions>
              </Form>
            </ModalContainer>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <Notification
        notifications={notifications}
        removeNotification={removeNotification}
      />
    </IntegrationsContainer>
  );
};

export default Integrations;
